// src/components/BrioComponents.js
// BRIO Design System - Reusable Components
// Requires: ../styles/brio-styles.css

import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Maximize2, Minimize2 } from 'lucide-react';

// ============================================================================
// PRESENTATION CODE MAPPING
// Maps codes like "1a", "2b", "3c" to presentation recipes
// Source of truth extracted from BrioStylePlayground.jsx
// ============================================================================

/**
 * BRIO_PRESENTATION_CODE_MAP
 * 
 * Code format: "{section}{variant}"
 *   section: 1-6 (profitability, markup, timeOff, leaveSummary, salaryMatrix, icons)
 *   variant: a=table default, b=table compact, c=row list
 * 
 * Each recipe contains:
 *   - kind: "table" | "rowList"
 *   - tableVariant: "default" | "compact" | "dense" (for tables)
 *   - stickyHeader: boolean (for tables)
 *   - striped: boolean (for tables)
 *   - maxHeight: number | null (for table wrapper scroll)
 *   - listGap: "xs" | "m" (for row lists)
 */
export const BRIO_PRESENTATION_CODE_MAP = {
  // Section 1: Profitability (hover)
  '1a': { kind: 'table', tableVariant: 'default', stickyHeader: true, striped: false, maxHeight: null, listGap: 'xs' },
  '1b': { kind: 'table', tableVariant: 'compact', stickyHeader: true, striped: false, maxHeight: null, listGap: 'xs' },
  '1c': { kind: 'rowList', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' },

  // Section 2: Markup (hover)
  '2a': { kind: 'table', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' },
  '2b': { kind: 'table', tableVariant: 'compact', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' },
  '2c': { kind: 'rowList', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' },

  // Section 3: Time Off (hover)
  '3a': { kind: 'table', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' },
  '3b': { kind: 'table', tableVariant: 'compact', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' },
  '3c': { kind: 'rowList', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' },

  // Section 4: Leave Summary
  '4a': { kind: 'table', tableVariant: 'dense', stickyHeader: false, striped: false, maxHeight: null, listGap: 'm' },
  '4b': { kind: 'table', tableVariant: 'compact', stickyHeader: false, striped: false, maxHeight: null, listGap: 'm' },
  '4c': { kind: 'rowList', tableVariant: 'dense', stickyHeader: false, striped: false, maxHeight: null, listGap: 'm' },

  // Section 5: Salary Matrix (hover)
  '5a': { kind: 'table', tableVariant: 'dense', stickyHeader: false, striped: true, maxHeight: null, listGap: 'm' },
  '5b': { kind: 'table', tableVariant: 'compact', stickyHeader: false, striped: true, maxHeight: null, listGap: 'm' },
  '5c': { kind: 'rowList', tableVariant: 'dense', stickyHeader: false, striped: false, maxHeight: null, listGap: 'm' },

  // Section 6: Icons
  '6a': { kind: 'table', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'm' },
  '6b': { kind: 'table', tableVariant: 'compact', stickyHeader: false, striped: false, maxHeight: null, listGap: 'm' },
  '6c': { kind: 'rowList', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'm' },
};

// Default fallback recipe
const DEFAULT_RECIPE = { kind: 'table', tableVariant: 'default', stickyHeader: false, striped: false, maxHeight: null, listGap: 'xs' };

/**
 * Get recipe from code
 * @param {string} code - e.g. "1a", "2b", "3c"
 * @returns {object} Recipe object
 */
export const getRecipeFromCode = (code) => {
  return BRIO_PRESENTATION_CODE_MAP[code] || DEFAULT_RECIPE;
};

/**
 * Convert variant id to code letter
 * @param {string} variantId - "default" | "compact" | "list"
 * @returns {string} "a" | "b" | "c"
 */
export const variantToLetter = (variantId) => {
  if (variantId === 'default') return 'a';
  if (variantId === 'compact') return 'b';
  if (variantId === 'list') return 'c';
  return 'a';
};

/**
 * Convert code letter to variant id
 * @param {string} letter - "a" | "b" | "c"
 * @returns {string} "default" | "compact" | "list"
 */
export const letterToVariant = (letter) => {
  if (letter === 'a') return 'default';
  if (letter === 'b') return 'compact';
  if (letter === 'c') return 'list';
  return 'default';
};

// ============================================================================
// MODAL COMPONENTS - BRIO-styled dialogs matching table 1a visual language
// ============================================================================

// Close icon for modal header
const CloseIcon = ({ size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * BrioModal - BRIO-styled modal dialog
 * 
 * Matches the visual language of BRIO table style "1a":
 * - White surface with subtle border
 * - Clean header with title and close button
 * - Consistent spacing
 * 
 * Features:
 * - Escape key closes modal
 * - Clicking backdrop closes modal
 * - Clicking inside modal does NOT close it
 * - Body scroll is locked while modal is open
 * 
 * @param {string} title - Modal title (string or JSX)
 * @param {boolean} titleDanger - Use red color for title (for delete confirmations)
 * @param {function} onClose - Close handler
 * @param {string} width - 'sm' | 'md' (default) | 'lg' | 'xl' | 'full'
 * @param {React.ReactNode} children - Modal body content
 * @param {React.ReactNode} footer - Footer content (buttons, etc.)
 * @param {string} className - Additional CSS classes
 */
export const BrioModal = ({
  title,
  titleDanger = false,
  onClose,
  width = 'md',
  children,
  footer,
  className = ''
}) => {
  // Escape key handler and body scroll lock
  useEffect(() => {
    // Store original overflow value to restore later
    const originalOverflow = document.body.style.overflow;
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    
    // Escape key handler
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  const modalClasses = [
    'brio-modal',
    `brio-modal--${width}`,
    className
  ].filter(Boolean).join(' ');

  const titleClasses = [
    'brio-modal-title',
    titleDanger && 'brio-modal-title--danger'
  ].filter(Boolean).join(' ');

  return (
    <div className="brio-modal-overlay" role="dialog" aria-modal="true">
      {/* Backdrop - clicking closes modal */}
      <div className="brio-modal-backdrop" onClick={onClose} />
      
      {/* Modal surface - clicking inside does NOT close */}
      <div className={modalClasses} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="brio-modal-header">
          <h3 className={titleClasses}>{title}</h3>
          <button
            type="button"
            className="brio-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="brio-modal-body">
          {children}
        </div>

        {/* Footer (optional) */}
        {footer && (
          <div className="brio-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * BrioModalBody - Modal body wrapper with form spacing
 * Use this inside BrioModal children for consistent form field spacing
 * 
 * @param {boolean} form - Enable form spacing (vertical gap between fields)
 * @param {string} className - Additional CSS classes
 */
export const BrioModalBody = ({ children, form = false, className = '' }) => {
  const classes = [
    form && 'brio-modal-body--form',
    className
  ].filter(Boolean).join(' ');

  return <div className={classes}>{children}</div>;
};

/**
 * BrioModalFooter - Footer layout helpers
 * These are CSS classes, but providing as reference:
 * - Use flex with gap for multiple buttons
 * - brio-btn brio-btn-primary for primary actions
 * - brio-btn brio-btn-secondary for cancel/dismiss
 * - brio-btn brio-btn-danger for destructive actions
 */

/**
 * BrioModalField - Form field wrapper with label and hint
 * 
 * @param {string} label - Field label
 * @param {string} hint - Optional hint text below the field
 * @param {string} className - Additional CSS classes
 */
export const BrioModalField = ({ children, label, hint, className = '' }) => {
  const classes = ['brio-modal-field', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {label && <label className="brio-modal-label">{label}</label>}
      {children}
      {hint && <p className="brio-modal-hint">{hint}</p>}
    </div>
  );
};

/**
 * BrioModalDivider - Section divider with optional title
 * 
 * @param {string} title - Optional section title
 */
export const BrioModalDivider = ({ title }) => {
  return (
    <div className="brio-modal-divider">
      {title && <p className="brio-modal-section-title">{title}</p>}
    </div>
  );
};

// ============================================================================
// LEGACY MODAL COMPONENTS (for backward compatibility)
// These are placeholder exports that App.js may already import
// ============================================================================

// These legacy components are kept for backward compatibility
// App.js already imports them but they weren't fully implemented
export const BrioCard = ({ children, className = '' }) => (
  <div className={`brio-analytics-surface ${className}`}>{children}</div>
);

export const BrioKpiBlock = ({ label, value, className = '' }) => (
  <div className={className}>
    <div className="brio-text-s brio-text-grey">{label}</div>
    <div className="brio-font-bold">{value}</div>
  </div>
);

export const BrioButton = ({ children, variant = 'primary', onClick, className = '', ...props }) => (
  <button 
    className={`brio-btn brio-btn-${variant} ${className}`} 
    onClick={onClick}
    {...props}
  >
    {children}
  </button>
);

export const BrioInfoBanner = ({ children, className = '' }) => (
  <div className={`brio-alert ${className}`}>{children}</div>
);

export const BrioEmptyState = ({ children, className = '' }) => (
  <div className={`brio-text-center brio-analytics-muted ${className}`} style={{ padding: '48px 0' }}>
    {children}
  </div>
);

export const BrioProgressBar = ({ value, variant, className = '' }) => (
  <BrioProgress value={value} variant={variant} className={className} />
);

export const BrioInput = ({ className = '', ...props }) => (
  <input className={`brio-modal-input ${className}`} {...props} />
);

export const BrioSelect = ({ children, className = '', ...props }) => (
  <select className={`brio-modal-select ${className}`} {...props}>{children}</select>
);

export const BrioGrid = ({ children, cols = 2, gap = 's', className = '' }) => (
  <div className={`brio-flex brio-flex-wrap brio-gap-${gap} ${className}`} style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
    {children}
  </div>
);

export const BrioSection = ({ children, title, className = '' }) => (
  <div className={className}>
    {title && <h3 className="brio-analytics-section-title brio-mb-s">{title}</h3>}
    {children}
  </div>
);

// ============================================================================
// FRAME WRAPPERS (FIX 3: Centered 875px frame for page content)
// ============================================================================

/**
 * BrioFrame - Centered 875px constrained container
 * Use this to wrap page sections that should be constrained to the standard width.
 * NOTE: This component ONLY handles centering/max-width. It does NOT control background.
 * 
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Content
 */
export const BrioFrame = ({ children, className = '' }) => {
  const classes = ['brio-frame', className].filter(Boolean).join(' ');
  return <div className={classes}>{children}</div>;
};

/**
 * useFullScreenPage - state + styling for a full-screen-capable page.
 *
 * Matches the Expense Claims page: when full-screen, the page becomes a fixed
 * white surface filling everything below the nav, so wide tables are no longer
 * constrained by the 875px BrioFrame. Spread `rootStyle` onto the page's root
 * element and append `rootClassName` to its className so nested BrioFrames
 * expand to full width (see `.brio-fullscreen-content .brio-frame` in
 * brio-styles.css).
 *
 * The surface is inset by `--brio-nav-height` (and `--brio-nav-bottom-height`
 * on mobile), which App.js publishes from its measured nav container. Full
 * screen means the page fills the content area, never that the main and
 * sub-page navigation disappear behind it. Both variables fall back to 0, so a
 * page rendered outside the app shell still covers the viewport.
 *
 * The z-index sits below the nav's own (50) for the same reason: a fixed
 * element creates a stacking context, so anything the page renders is capped
 * by this value, and a page stacked above the nav would swallow the nav's
 * dropdowns and the mobile sub-page sheet. In-page popups therefore stay under
 * the nav band, and since they open below their trigger that never shows.
 *
 * @param {boolean} defaultOn - initial full-screen state
 * @returns {{ isFullScreen, setIsFullScreen, toggle, rootStyle, rootClassName }}
 */
export const useFullScreenPage = (defaultOn = false) => {
  const [isFullScreen, setIsFullScreen] = useState(defaultOn);
  const toggle = useCallback(() => setIsFullScreen((v) => !v), []);
  const rootStyle = isFullScreen
    ? { position: 'fixed', top: 'var(--brio-nav-height, 0px)', left: 0, right: 0, bottom: 'var(--brio-nav-bottom-height, 0px)', zIndex: 40, backgroundColor: '#fff', display: 'flex', flexDirection: 'column', overflow: 'auto' }
    : {};
  const rootClassName = isFullScreen ? 'brio-fullscreen-content' : '';
  return { isFullScreen, setIsFullScreen, toggle, rootStyle, rootClassName };
};

/**
 * BrioFullScreenButton - icon toggle button, identical to the Expense Claims page.
 */
export const BrioFullScreenButton = ({ isFullScreen, onToggle, style = {} }) => (
  <button
    type="button"
    onClick={onToggle}
    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--brio-grey, #888)', ...style }}
    title={isFullScreen ? 'Exit full screen' : 'Full screen'}
  >
    {isFullScreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
  </button>
);

/**
 * BrioFrameHeader - Page title + actions row within 875px frame
 * Use this for consistent page header layouts.
 * 
 * @param {string} title - Page or section title
 * @param {React.ReactNode} right - Right-side content (buttons, etc.)
 * @param {string} className - Additional CSS classes
 */
export const BrioFrameHeader = ({ title, right = null, className = '' }) => {
  const classes = ['brio-frame-header', className].filter(Boolean).join(' ');
  return (
    <div className={classes}>
      <div className="brio-frame-title">{title}</div>
      {right && <div className="brio-frame-actions">{right}</div>}
    </div>
  );
};

// ============================================================================
// LAYOUT WRAPPERS
// ============================================================================

/**
 * BrioTableWrapper - Constrains table width to 875px max, centered, supports scrolling
 * 
 * FIX 1: Now centered with margin auto
 * FIX 2: Only applies vertical scroll when maxHeight is provided
 * 
 * @param {number} maxHeight - Optional max height for vertical scrolling (only when needed)
 * @param {string} variant - 'plain' (default) | 'card' (bordered surface)
 * @param {string} className - Additional CSS classes
 */
export const BrioTableWrapper = ({ 
  children, 
  maxHeight, 
  variant = 'plain', 
  className = '' 
}) => {
  // FIX 2: Only add scroll class when maxHeight is explicitly provided
  const hasMaxHeight = maxHeight !== undefined && maxHeight !== null;
  
  const classes = [
    'brio-table-wrap',
    hasMaxHeight && 'brio-table-wrap--scroll',
    variant === 'card' && 'brio-table-wrap--card',
    className
  ].filter(Boolean).join(' ');

  // Only apply maxHeight style when provided
  const style = hasMaxHeight ? { maxHeight } : undefined;

  return (
    <div className={classes} style={style}>
      {children}
    </div>
  );
};

/**
 * BrioListWrapper - Column layout with gap, constrained to 875px max, centered
 * 
 * FIX 1: Now centered with margin auto
 * 
 * @param {string} gap - Gap size: 'xs' (8px) or 'm' (12px)
 * @param {string} className - Additional CSS classes
 */
export const BrioListWrapper = ({ children, gap = 'xs', className = '' }) => {
  const classes = [
    'brio-list-wrap',
    gap === 'm' && 'brio-list-wrap--gap-m',
    className
  ].filter(Boolean).join(' ');

  return <div className={classes}>{children}</div>;
};

/**
 * BrioAnalyticsSurface - Flat bordered surface for analytics/admin sections
 * @param {string} padding - Padding size: 'xs' | 's' | 'm' (default) | 'l'
 * @param {string} className - Additional CSS classes
 */
export const BrioAnalyticsSurface = ({ children, padding = 'm', className = '' }) => {
  const classes = [
    'brio-analytics-surface',
    `brio-analytics-surface--${padding}`,
    className
  ].filter(Boolean).join(' ');
  
  return <div className={classes}>{children}</div>;
};

/**
 * BrioSurface - Backwards-compatible alias for BrioAnalyticsSurface
 * Used by CalendarPage.jsx and other legacy imports
 * @param {string} padding - Padding size: 'xs' | 's' | 'm' (default) | 'l'
 * @param {string} className - Additional CSS classes
 */
export const BrioSurface = BrioAnalyticsSurface;

// ============================================================================
// PAGE HEADERS & NAVIGATION
// ============================================================================

/**
 * BrioSectionHeader - Section header with title and optional right slot
 * @param {string} title - Section title text
 * @param {React.ReactNode} right - Optional right-side content (buttons, etc.)
 * @param {string} className - Additional CSS classes
 */
export const BrioSectionHeader = ({ title, right, className = '' }) => {
  const classes = ['brio-section-header', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <h2 className="brio-analytics-section-title">{title}</h2>
      {right && <div className="brio-section-header__right">{right}</div>}
    </div>
  );
};

/**
 * BrioPageSubHeader - Horizontal submenu bar with tabs/buttons
 * Used by App.js, AnalyticsDashboardPage.jsx for page-level navigation
 * 
 * HEADER ALIGNMENT FIX:
 * - OUTER div (.brio-pageband--header) spans FULL viewport width with white background
 * - INNER BrioFrame constrains content to 875px centered
 * 
 * @param {Array} items - Array of { id|key|value|slug, label|name, icon?, onClick?, href?, badge? }
 *   badge?: number - when > 0, renders a small red counter dot after the label
 * @param {string} activeKey - Currently active item key
 * @param {function} onSelect - Callback when item is selected: (key) => void
 * @param {string} variant - 'global' (default) | 'local' - visual variant
 * @param {React.ReactNode} right - Optional right-side content
 * @param {boolean} sticky - Enable sticky positioning
 * @param {number|string} top - Top offset for sticky (default: 0 or var(--app-header-h) if available)
 * @param {string} className - Additional CSS classes
 */
export const BrioPageSubHeader = ({ 
  items = [], 
  activeKey, 
  onSelect,
  variant = 'global',
  right,
  sticky = false,
  top,
  className = '' 
}) => {
  // OUTER band classes: full-width with white background, border, sticky
  const bandClasses = [
    'brio-pageband',
    'brio-pageband--header',
    sticky && 'brio-pageband--sticky',
    variant === 'local' && 'brio-pageband--local',
    className
  ].filter(Boolean).join(' ');

  // Determine sticky top value
  let stickyTop = 0;
  if (sticky) {
    if (top !== undefined) {
      stickyTop = top;
    }
  }

  const bandStyle = sticky ? { top: stickyTop } : undefined;

  return (
    // OUTER: Full-width band with white background, border, sticky
    <div className={bandClasses} style={bandStyle}>
      {/* INNER: BrioFrame constrains content to 875px centered */}
      <BrioFrame>
        <div className="brio-page-subheader__content">
          <div className="brio-page-subheader__tabs">
            {items.map((item, index) => {
              // Normalize item key: support id, key, value, slug, or fall back to label
              const itemKey = item.id ?? item.key ?? item.value ?? item.slug ?? item.label ?? index;
              // Normalize label: support label, name, or stringify the key
              const itemLabel = item.label ?? item.name ?? String(itemKey);
              
              // Determine if this item is active
              const isActive = String(activeKey) === String(itemKey);
              
              const tabClasses = [
                'brio-page-subheader__tab',
                isActive && 'brio-page-subheader__tab--active'
              ].filter(Boolean).join(' ');

              const handleClick = () => {
                // Priority: item.onClick > onSelect > href navigation
                if (item.onClick) {
                  item.onClick();
                } else if (onSelect) {
                  onSelect(itemKey);
                } else if (item.href) {
                  window.location.assign(item.href);
                }
              };

              return (
                <button
                  key={itemKey}
                  type="button"
                  className={tabClasses}
                  onClick={handleClick}
                >
                  {item.icon && <span className="brio-page-subheader__tab-icon">{item.icon}</span>}
                  <span>{itemLabel}</span>
                  {item.badge > 0 && (
                    <span
                      className="brio-page-subheader__tab-badge"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--brio-orange)',
                        color: '#ffffff',
                        fontSize: '9px',
                        fontWeight: 700,
                        lineHeight: 1,
                        padding: '0 3px',
                        marginLeft: '5px'
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {right && <div className="brio-page-subheader__right">{right}</div>}
        </div>
      </BrioFrame>
    </div>
  );
};

/**
 * BrioPageHeader - Full-width page header band with centered content
 * 
 * HEADER ALIGNMENT FIX:
 * - OUTER div (.brio-pageband--header) spans FULL viewport width with white background
 * - INNER BrioFrame constrains content to 875px centered
 * 
 * @param {string} title - Page title
 * @param {string} subtitle - Optional subtitle text
 * @param {React.ReactNode} right - Right-side content (buttons, etc.)
 * @param {boolean} sticky - Enable sticky positioning
 * @param {number|string} top - Top offset for sticky
 * @param {string} className - Additional CSS classes
 */
export const BrioPageHeader = ({
  title,
  subtitle,
  right,
  sticky = false,
  top = 0,
  className = ''
}) => {
  const bandClasses = [
    'brio-pageband',
    'brio-pageband--header',
    sticky && 'brio-pageband--sticky',
    className
  ].filter(Boolean).join(' ');

  const bandStyle = sticky ? { top } : undefined;

  return (
    // OUTER: Full-width band with white background
    <div className={bandClasses} style={bandStyle}>
      {/* INNER: BrioFrame constrains content to 875px centered */}
      <BrioFrame>
        <div className="brio-page-header__content">
          <div className="brio-page-header__left">
            {title && <h1 className="brio-page-header__title">{title}</h1>}
            {subtitle && <p className="brio-page-header__subtitle">{subtitle}</p>}
          </div>
          {right && <div className="brio-page-header__right">{right}</div>}
        </div>
      </BrioFrame>
    </div>
  );
};

// ============================================================================
// BRIO BY CODE - Central renderer component
// ============================================================================

/*
 * ============================================================================
 * PAGE USAGE PATTERN
 * ============================================================================
 * 
 * How to use presentation codes (1a–6c) in real pages:
 * 
 *   import { BrioByCode, BrioTable, getTablePropsFromCode, isListCode } from './BrioComponents';
 * 
 *   const code = "1b";  // e.g. from props or config
 * 
 *   // For table variants (a, b):
 *   const tableProps = getTablePropsFromCode(code);
 * 
 *   <BrioByCode code={code}>
 *     <BrioTable {...tableProps}>
 *       <thead>...</thead>
 *       <tbody>...</tbody>
 *     </BrioTable>
 *   </BrioByCode>
 * 
 *   // For list variants (c):
 *   if (isListCode(code)) {
 *     <BrioByCode code={code}>
 *       <BrioListItem>...</BrioListItem>
 *       <BrioListItem>...</BrioListItem>
 *     </BrioByCode>
 *   }
 * 
 * Responsibilities:
 *   - BrioByCode: controls layout wrapper (BrioTableWrapper or BrioListWrapper)
 *   - getTablePropsFromCode: returns { variant, stickyHeader, striped } for BrioTable
 *   - isListCode: returns true if the code is a row list variant (c)
 * 
 * ============================================================================
 */

/**
 * BrioByCode - Renders the appropriate wrapper based on presentation code
 * 
 * FIX 2: Only passes maxHeight to BrioTableWrapper when recipe explicitly defines it
 * 
 * @param {string} code - Presentation code e.g. "1a", "2b", "3c"
 * @param {string} className - Additional CSS classes
 * @param {object} wrapperProps - Props forwarded to wrapper (maxHeight for table, gap for list)
 * @param {React.ReactNode} children - Content to render (table or list items)
 * 
 * Note: This component provides the WRAPPER layer only.
 * The children should be the actual table/list content.
 * For tables: children = <BrioTable {...getTablePropsFromCode(code)}>...</BrioTable>
 * For lists: children = BrioListItem components
 */
export const BrioByCode = ({
  code,
  className = '',
  wrapperProps = {},
  children
}) => {
  const recipe = getRecipeFromCode(code);

  if (recipe.kind === 'rowList') {
    const gap = wrapperProps.gap || recipe.listGap;
    return (
      <BrioListWrapper gap={gap} className={className}>
        {children}
      </BrioListWrapper>
    );
  }

  // Default to table kind
  // FIX 2: Only use maxHeight if explicitly provided in wrapperProps OR in recipe
  // If recipe.maxHeight is null, don't pass maxHeight (no forced scroll)
  const maxHeight = wrapperProps.maxHeight !== undefined 
    ? wrapperProps.maxHeight 
    : recipe.maxHeight;
  const wrapperVariant = wrapperProps.variant || 'plain';

  return (
    <BrioTableWrapper maxHeight={maxHeight} variant={wrapperVariant} className={className}>
      {children}
    </BrioTableWrapper>
  );
};

/**
 * getTablePropsFromCode - Extract BrioTable props from a presentation code
 * 
 * Returns an object with { variant, stickyHeader, striped } that can be
 * spread directly onto BrioTable:
 * 
 *   const tableProps = getTablePropsFromCode("1b");
 *   <BrioTable {...tableProps}>...</BrioTable>
 * 
 * @param {string} code - Presentation code e.g. "1a", "2b"
 * @returns {object} { variant, stickyHeader, striped }
 */
export const getTablePropsFromCode = (code) => {
  const recipe = getRecipeFromCode(code);
  return {
    variant: recipe.tableVariant,
    stickyHeader: recipe.stickyHeader,
    striped: recipe.striped,
  };
};

/**
 * Helper to check if code is a list variant
 */
export const isListCode = (code) => {
  const recipe = getRecipeFromCode(code);
  return recipe.kind === 'rowList';
};

// ============================================================================
// TABLE COMPONENTS
// ============================================================================

/**
 * BrioTable - Styled table with variant support
 * @param {string} variant - 'default' | 'compact' | 'dense'
 * @param {boolean} stickyHeader - Enable sticky header
 * @param {boolean} striped - Enable zebra striping
 * @param {string} className - Additional CSS classes
 */
export const BrioTable = ({
  children,
  variant = 'default',
  stickyHeader = false,
  striped = false,
  className = '',
  style
}) => {
  const classes = [
    'brio-table',
    `brio-table--${variant}`,
    stickyHeader && 'brio-table--sticky',
    striped && 'brio-table--striped',
    className
  ].filter(Boolean).join(' ');

  return <table className={classes} style={style}>{children}</table>;
};

// ============================================================================
// LIST COMPONENTS
// ============================================================================

/**
 * BrioListItem - Row item for list view with variant support
 * @param {string} variant - 'default' | 'row' | 'compact' | 'dense' | 'alert'
 * @param {string} tone - For alert variant: 'gold' | 'red' | 'orange' | 'yellow' | 'green' | 'grey'
 * @param {string} stripeColor - Custom stripe color (CSS value) - takes precedence over tone
 * @param {boolean} hoverable - Enable hover highlight
 * @param {boolean} hovered - Controlled hover state
 * @param {string} className - Additional CSS classes
 */
export const BrioListItem = ({
  children,
  variant = 'default',
  tone,
  stripeColor,
  hoverable = false,
  hovered = false,
  className = '',
  style,
  onMouseEnter,
  onMouseLeave,
  onClick
}) => {
  const isAlert = variant === 'alert';
  
  const classes = [
    'brio-list-item',
    `brio-list-item--${variant}`,
    hoverable && 'brio-list-item--hoverable',
    hovered && 'brio-list-item--hovered',
    isAlert && tone && `brio-tone--${tone}`,
    className
  ].filter(Boolean).join(' ');

  // Support custom stripe color via CSS variable
  const itemStyle = stripeColor 
    ? { ...style, '--brio-stripe': stripeColor } 
    : style;

  return (
    <div
      className={classes}
      style={itemStyle}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {isAlert && <div className="brio-list-item__stripe" />}
      {children}
    </div>
  );
};

// ============================================================================
// TEAM & USER ICONS
// ============================================================================

/**
 * BrioTeamIcon - Circular ring indicator for team color
 * @param {string} color - Border color (CSS value)
 * @param {number} size - Width/height in pixels (default: 24)
 * @param {number} ring - Border width in pixels (default: 4)
 * @param {string} title - Tooltip text
 * @param {string} className - Additional CSS classes
 */
export const BrioTeamIcon = ({
  color,
  size = 24,
  ring = 6,
  title,
  className = ''
}) => {
  const classes = ['brio-team-icon', className].filter(Boolean).join(' ');

  return (
    <div
      className={classes}
      style={{
        width: size,
        height: size,
        borderWidth: ring,
        borderColor: color
      }}
      title={title}
    />
  );
};

/**
 * BrioUserIcon - Avatar with allocation/progress ring
 * @param {string} initials - User initials (1-2 chars)
 * @param {string} ringColor - SVG stroke color for progress ring
 * @param {string} fillColor - Background color for inner circle
 * @param {number} size - Overall size in pixels (default: 44)
 * @param {number} ringWidth - Stroke width in pixels (default: 4)
 * @param {number} progress - Progress percentage 0-100 (default: 0, shows gray background)
 * @param {string} ringBgColor - Background ring color (default: #e5e7eb)
 * @param {string} title - Tooltip text
 * @param {string} className - Additional CSS classes
 */
export const BrioUserIcon = ({
  initials,
  ringColor,
  fillColor,
  size = 44,
  ringWidth = 6,
  progress = 0,
  ringBgColor = '#e5e7eb',
  title,
  className = ''
}) => {
  const classes = ['brio-user-icon', className].filter(Boolean).join(' ');
  
  // SVG ring geometry
  const radius = (size - ringWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const cappedProgress = Math.min(100, Math.max(0, progress));
  const strokeDashoffset = circumference - (cappedProgress / 100) * circumference;
  
  // Inner circle dimensions
  const innerSize = size - (ringWidth * 2);

  return (
    <div
      className={classes}
      style={{ width: size, height: size }}
      title={title}
    >
      {/* SVG Ring */}
      <svg
        className="brio-user-icon__ring"
        width={size}
        height={size}
        style={{ transform: 'rotate(-90deg)' }}
      >
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringBgColor}
          strokeWidth={ringWidth}
        />
        {/* Progress ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={ringWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      
      {/* Inner circle with initials */}
      <div
        className="brio-user-icon__inner"
        style={{
          top: ringWidth,
          left: ringWidth,
          width: innerSize,
          height: innerSize,
          backgroundColor: fillColor
        }}
      >
        {initials}
      </div>
    </div>
  );
};

// ============================================================================
// ICON BUTTONS
// ============================================================================

// Icon size mapping
const ICON_SIZES = {
  small: 12,
  default: 14,
  large: 16
};

// SVG Icons
const EditIcon = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

const DeleteIcon = ({ size }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const ICONS = {
  edit: EditIcon,
  delete: DeleteIcon
};

/**
 * BrioIconButton - Minimal icon button for edit/delete actions
 * @param {string} icon - 'edit' | 'delete'
 * @param {string} variant - 'default' | 'edit' | 'delete' (affects hover color)
 * @param {string} size - 'small' | 'default' | 'large'
 * @param {string} title - Tooltip text
 * @param {boolean} disabled - Disabled state
 * @param {function} onClick - Click handler
 */
export const BrioIconButton = ({
  icon,
  variant = 'default',
  size = 'default',
  title,
  disabled = false,
  onClick,
  className = ''
}) => {
  const iconSize = ICON_SIZES[size] || ICON_SIZES.default;
  const IconComponent = ICONS[icon];

  const classes = [
    'brio-icon-btn',
    variant !== 'default' && `brio-icon-btn--${variant}`,
    size !== 'default' && `brio-icon-btn--${size}`,
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      className={classes}
      onClick={onClick}
      disabled={disabled}
      title={title}
      type="button"
    >
      {IconComponent ? <IconComponent size={iconSize} /> : icon}
    </button>
  );
};

/**
 * BrioActionGroup - Horizontal group for action buttons
 * @param {boolean} center - Center align buttons
 * @param {string} className - Additional CSS classes
 */
export const BrioActionGroup = ({ children, center = false, className = '' }) => {
  const classes = [
    'brio-action-group',
    center && 'brio-action-group--center',
    className
  ].filter(Boolean).join(' ');

  return <div className={classes}>{children}</div>;
};

// ============================================================================
// STATUS BADGES
// ============================================================================

/**
 * BrioBadge - Status badge with semantic colors (all from brioProjectColors palettes)
 * @param {string} variant - 'approved' | 'pending' | 'warning' | 'danger' | 'info' | 'success' | 'neutral' | 'caution'
 * @param {string} className - Additional CSS classes
 */
export const BrioBadge = ({ children, variant = 'pending', className = '' }) => {
  const classes = [
    'brio-badge',
    `brio-badge--${variant}`,
    className
  ].filter(Boolean).join(' ');

  return <span className={classes}>{children}</span>;
};

// ============================================================================
// COLOR INDICATOR
// ============================================================================

/**
 * BrioColorIndicator - Vertical color bar for project identification in tables
 * @param {string} color - Background color (CSS value)
 * @param {string} size - 'default' | 'compact'
 */
export const BrioColorIndicator = ({ color, size = 'default', className = '' }) => {
  const classes = [
    'brio-color-indicator',
    `brio-color-indicator--${size}`,
    className
  ].filter(Boolean).join(' ');

  return <div className={classes} style={{ background: color }} />;
};

// ============================================================================
// ALERT / NOTE BOX
// ============================================================================

/**
 * BrioAlert - Info/note box with gold styling
 * @param {string} label - Optional label (e.g., "note:")
 * @param {string} className - Additional CSS classes
 */
export const BrioAlert = ({ children, label, className = '' }) => {
  const classes = ['brio-alert', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {label && <span className="brio-alert__label">{label} </span>}
      <span className="brio-alert__text">{children}</span>
    </div>
  );
};

// ============================================================================
// HIGHLIGHT BOX
// ============================================================================

/**
 * @deprecated Use BrioKpiBlock instead
 * BrioHighlightBox - Prominent display box (e.g., for active markup)
 * @param {string} label - Label text
 * @param {string} value - Main value
 * @param {string} className - Additional CSS classes
 */
export const BrioHighlightBox = ({ label, value, className = '' }) => {
  const classes = ['brio-highlight-box', className].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <div className="brio-highlight-box__label">{label}</div>
      <div className="brio-highlight-box__value">{value}</div>
    </div>
  );
};

// ============================================================================
// PROGRESS BAR
// ============================================================================

/**
 * BrioProgress - Progress bar with semantic colors
 * @param {number} value - Progress percentage (0-100)
 * @param {string} variant - 'normal' | 'warning' | 'danger' (auto-detected if not provided)
 * @param {boolean} tall - Use 6px height instead of 4px
 * @param {string} className - Additional CSS classes
 */
export const BrioProgress = ({ value, variant, tall = false, className = '' }) => {
  // Auto-detect variant based on value if not provided
  const autoVariant = variant || (value > 100 ? 'danger' : value > 80 ? 'warning' : 'normal');

  const wrapperClasses = [
    'brio-progress',
    tall && 'brio-progress--tall',
    className
  ].filter(Boolean).join(' ');

  const fillClasses = [
    'brio-progress__fill',
    `brio-progress__fill--${autoVariant}`
  ].join(' ');

  return (
    <div className={wrapperClasses}>
      <div
        className={fillClasses}
        style={{ width: `${Math.min(value, 100)}%` }}
      />
    </div>
  );
};

// ============================================================================
// CSS VARIABLE REFERENCES (for JS usage when needed)
// ============================================================================

// ============================================================================
// DATE PICKER UTILITIES
// ============================================================================

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LABELS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1; // Monday=0
}

function toDateStr(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function parseDateStr(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length !== 3) return null;
  return { year: parseInt(parts[0]), month: parseInt(parts[1]) - 1, day: parseInt(parts[2]) };
}

function parseMonthStr(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length < 2) return null;
  return { year: parseInt(parts[0]), month: parseInt(parts[1]) - 1 };
}

function formatDisplay(dateStr, placeholder) {
  const p = parseDateStr(dateStr);
  if (!p) return placeholder !== undefined ? placeholder : 'Select date';
  return `${String(p.day).padStart(2, '0')}-${MONTH_LABELS[p.month]}-${p.year}`;
}

function formatMonthDisplay(monthStr, placeholder) {
  const p = parseMonthStr(monthStr);
  if (!p) return placeholder || 'Select month';
  return `${MONTH_LABELS_FULL[p.month]} ${p.year}`;
}

function isSameDay(a, b) {
  return a && b && a.year === b.year && a.month === b.month && a.day === b.day;
}

function todayObj() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
}

// ============================================================================
// useClickOutside hook (internal)
// ============================================================================
function useClickOutside(ref, handler) {
  useEffect(() => {
    const listener = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

// Calendar icon SVG (inline to avoid dependency)
const CalendarIcon = () => (
  <svg className="brio-datepicker-trigger__icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <rect x="1.5" y="2.5" width="13" height="12" rx="0" />
    <line x1="1.5" y1="6.5" x2="14.5" y2="6.5" />
    <line x1="5" y1="1" x2="5" y2="4" />
    <line x1="11" y1="1" x2="11" y2="4" />
  </svg>
);

const ChevronLeft = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 3L5 7L9 11" /></svg>
);
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M5 3L9 7L5 11" /></svg>
);

// ============================================================================
// BrioDatePicker - Apple Calendar-inspired single date picker
// ============================================================================

/**
 * BrioDatePicker
 * @param {string} value - ISO date string (YYYY-MM-DD)
 * @param {function} onChange - called with ISO date string or ''
 * @param {string} [placeholder] - placeholder text
 * @param {string} [className] - extra class on wrapper
 * @param {boolean} [disabled] - disable interaction
 * @param {boolean} [clearable] - show clear button (default true)
 * @param {object} [style] - inline style on trigger
 * @param {string} [min] - minimum date (YYYY-MM-DD)
 * @param {string} [max] - maximum date (YYYY-MM-DD)
 */
export function BrioDatePicker({ value, onChange, placeholder, className = '', disabled, clearable = true, style, triggerStyle, min, max, openAbove = false, centerAlign = false, as: TriggerTag = 'button', ...rest }) {
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('days'); // 'days' | 'months' | 'years'
  const parsed = parseDateStr(value);
  const today = todayObj();
  const [viewYear, setViewYear] = useState(parsed ? parsed.year : today.year);
  const [viewMonth, setViewMonth] = useState(parsed ? parsed.month : today.month);
  const ref = useRef(null);

  const closeHandler = useCallback(() => { setOpen(false); setViewMode('days'); }, []);
  useClickOutside(ref, closeHandler);

  // Sync view when value changes externally
  useEffect(() => {
    const p = parseDateStr(value);
    if (p) { setViewYear(p.year); setViewMonth(p.month); }
  }, [value]);

  const minP = parseDateStr(min);
  const maxP = parseDateStr(max);

  function isDayDisabled(y, m, d) {
    if (minP) {
      const v = y * 10000 + m * 100 + d;
      const mv = minP.year * 10000 + minP.month * 100 + minP.day;
      if (v < mv) return true;
    }
    if (maxP) {
      const v = y * 10000 + m * 100 + d;
      const mv = maxP.year * 10000 + maxP.month * 100 + maxP.day;
      if (v > mv) return true;
    }
    return false;
  }

  function selectDay(day, monthOffset) {
    let m = viewMonth + monthOffset;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    onChange(toDateStr(y, m, day));
    setOpen(false);
    setViewMode('days');
  }

  function navMonth(delta) {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setViewMonth(m);
    setViewYear(y);
  }

  function selectMonth(m) {
    setViewMonth(m);
    setViewMode('days');
  }

  function selectYear(y) {
    setViewYear(y);
    setViewMode('months');
  }

  function goToday() {
    onChange(toDateStr(today.year, today.month, today.day));
    setOpen(false);
    setViewMode('days');
  }

  // Build calendar grid
  function renderDays() {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
    const daysInPrev = getDaysInMonth(prevYear, prevMonth);
    const cells = [];

    // Previous month trailing days
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = daysInPrev - i;
      const dis = isDayDisabled(prevYear, prevMonth, d);
      cells.push(
        <button key={`p${d}`} type="button"
          className={`brio-datepicker-day brio-datepicker-day--outside${dis ? ' brio-datepicker-day--disabled' : ''}`}
          onClick={() => !dis && selectDay(d, -1)}>{d}</button>
      );
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      const isToday = viewYear === today.year && viewMonth === today.month && d === today.day;
      const isSelected = parsed && isSameDay(parsed, { year: viewYear, month: viewMonth, day: d });
      const dis = isDayDisabled(viewYear, viewMonth, d);
      let cls = 'brio-datepicker-day';
      if (isSelected) cls += ' brio-datepicker-day--selected';
      else if (isToday) cls += ' brio-datepicker-day--today';
      if (dis) cls += ' brio-datepicker-day--disabled';
      cells.push(
        <button key={d} type="button" className={cls}
          onClick={() => !dis && selectDay(d, 0)}>{d}</button>
      );
    }

    // Next month leading days
    const totalCells = cells.length;
    const remaining = (7 - (totalCells % 7)) % 7;
    for (let d = 1; d <= remaining; d++) {
      const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
      const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
      const dis = isDayDisabled(nextYear, nextMonth, d);
      cells.push(
        <button key={`n${d}`} type="button"
          className={`brio-datepicker-day brio-datepicker-day--outside${dis ? ' brio-datepicker-day--disabled' : ''}`}
          onClick={() => !dis && selectDay(d, 1)}>{d}</button>
      );
    }

    return (
      <>
        <div className="brio-datepicker-weekdays">
          {WEEKDAY_LABELS.map(w => <div key={w} className="brio-datepicker-weekday">{w}</div>)}
        </div>
        <div className="brio-datepicker-days">{cells}</div>
      </>
    );
  }

  function renderMonths() {
    return (
      <div className="brio-datepicker-months">
        {MONTH_LABELS.map((label, i) => {
          let cls = 'brio-datepicker-month-cell';
          if (i === viewMonth && viewYear === today.year && i === today.month) cls += ' brio-datepicker-month-cell--current';
          if (i === viewMonth) cls += ' brio-datepicker-month-cell--selected';
          return <button key={i} type="button" className={cls} onClick={() => selectMonth(i)}>{label}</button>;
        })}
      </div>
    );
  }

  function renderYears() {
    const base = viewYear - 4;
    return (
      <div className="brio-datepicker-years">
        {Array.from({ length: 12 }, (_, i) => {
          const y = base + i;
          let cls = 'brio-datepicker-year-cell';
          if (y === today.year) cls += ' brio-datepicker-year-cell--current';
          if (y === viewYear) cls += ' brio-datepicker-year-cell--selected';
          return <button key={y} type="button" className={cls} onClick={() => selectYear(y)}>{y}</button>;
        })}
      </div>
    );
  }

  const triggerCls = `brio-datepicker-trigger${!value ? ' brio-datepicker-trigger--placeholder' : ''}${open ? ' brio-datepicker-trigger--open' : ''} ${className}`;

  // Compute fixed position for the dropdown to escape stacking contexts (e.g. sticky table headers).
  // Auto-flips above the trigger when there is insufficient space below the viewport fold.
  const PICKER_HEIGHT_ESTIMATE = 290; // calendar popup is ~290px tall
  const PICKER_WIDTH_ESTIMATE = 280;  // matches min-width in CSS
  const [dropdownPos, setDropdownPos] = useState(null);
  useEffect(() => {
    if (!open || !ref.current) { setDropdownPos(null); return; }
    const rect = ref.current.getBoundingClientRect();
    // Use visualViewport when available (iOS handles dynamic address-bar height correctly)
    const vph = window.visualViewport ? window.visualViewport.height : window.innerHeight;
    const spaceBelow = vph - rect.bottom - 4;
    const shouldOpenAbove = openAbove || spaceBelow < PICKER_HEIGHT_ESTIMATE;
    const leftBase = centerAlign
      ? rect.left + rect.width / 2 - PICKER_WIDTH_ESTIMATE / 2
      : rect.left;
    // Clamp left so the picker never overflows the right edge of the viewport
    const vpw = window.visualViewport ? window.visualViewport.width : window.innerWidth;
    const clampedLeft = Math.max(4, Math.min(leftBase, vpw - PICKER_WIDTH_ESTIMATE - 4));
    if (shouldOpenAbove) {
      setDropdownPos({ bottom: vph - rect.top + 4, left: clampedLeft });
    } else {
      setDropdownPos({ top: rect.bottom + 4, left: clampedLeft });
    }
  }, [open, openAbove, centerAlign]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', ...style }} {...rest}>
      <TriggerTag
        type={TriggerTag === 'button' ? 'button' : undefined}
        role={TriggerTag !== 'button' ? 'button' : undefined}
        tabIndex={TriggerTag !== 'button' ? 0 : undefined}
        className={triggerCls}
        style={triggerStyle}
        onClick={() => !disabled && setOpen(!open)}
        disabled={TriggerTag === 'button' ? disabled : undefined}
      >
        {!value && <CalendarIcon />}
        <span>{formatDisplay(value, placeholder)}</span>
      </TriggerTag>
      {open && dropdownPos && ReactDOM.createPortal(
        <div
          className="brio-datepicker-dropdown"
          style={{ position: 'fixed', ...dropdownPos }}
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
        >
          <div className="brio-datepicker-header">
            <div className="brio-datepicker-nav">
              {viewMode === 'days' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => navMonth(-1)}><ChevronLeft /></button>}
              {viewMode === 'years' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y - 12)}><ChevronLeft /></button>}
            </div>
            <span
              className="brio-datepicker-header__title"
              onClick={() => setViewMode(viewMode === 'days' ? 'months' : viewMode === 'months' ? 'years' : 'days')}
            >
              {viewMode === 'days' && `${MONTH_LABELS_FULL[viewMonth]} ${viewYear}`}
              {viewMode === 'months' && `${viewYear}`}
              {viewMode === 'years' && `${viewYear - 4} – ${viewYear + 7}`}
            </span>
            <div className="brio-datepicker-nav">
              {viewMode === 'days' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => navMonth(1)}><ChevronRight /></button>}
              {viewMode === 'years' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y + 12)}><ChevronRight /></button>}
            </div>
          </div>
          {viewMode === 'days' && renderDays()}
          {viewMode === 'months' && renderMonths()}
          {viewMode === 'years' && renderYears()}
          <div className="brio-datepicker-footer">
            <button type="button" className="brio-datepicker-footer__btn" onClick={goToday}>Today</button>
            {clearable && value && <button type="button" className="brio-datepicker-footer__btn" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ============================================================================
// BrioMonthPicker - Month-level selection (YYYY-MM)
// ============================================================================

/**
 * BrioMonthPicker
 * @param {string} value - Month string (YYYY-MM)
 * @param {function} onChange - called with YYYY-MM string or ''
 * @param {string} [placeholder]
 * @param {string} [className]
 * @param {boolean} [disabled]
 * @param {boolean} [clearable]
 * @param {object} [style]
 */
export function BrioMonthPicker({ value, onChange, placeholder, className = '', disabled, clearable = true, formatDisplay, style, ...rest }) {
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('months'); // 'months' | 'years'
  const parsed = parseMonthStr(value);
  const today = todayObj();
  const [viewYear, setViewYear] = useState(parsed ? parsed.year : today.year);
  const ref = useRef(null);

  const closeHandler = useCallback(() => { setOpen(false); setViewMode('months'); }, []);
  useClickOutside(ref, closeHandler);

  useEffect(() => {
    const p = parseMonthStr(value);
    if (p) setViewYear(p.year);
  }, [value]);

  function selectMonth(m) {
    onChange(`${viewYear}-${String(m + 1).padStart(2, '0')}`);
    setOpen(false);
    setViewMode('months');
  }

  function selectYear(y) {
    setViewYear(y);
    setViewMode('months');
  }

  function renderMonths() {
    return (
      <div className="brio-datepicker-months">
        {MONTH_LABELS.map((label, i) => {
          let cls = 'brio-datepicker-month-cell';
          if (parsed && parsed.year === viewYear && parsed.month === i) cls += ' brio-datepicker-month-cell--selected';
          else if (viewYear === today.year && i === today.month) cls += ' brio-datepicker-month-cell--current';
          return <button key={i} type="button" className={cls} onClick={() => selectMonth(i)}>{label}</button>;
        })}
      </div>
    );
  }

  function renderYears() {
    const base = viewYear - 4;
    return (
      <div className="brio-datepicker-years">
        {Array.from({ length: 12 }, (_, i) => {
          const y = base + i;
          let cls = 'brio-datepicker-year-cell';
          if (y === today.year) cls += ' brio-datepicker-year-cell--current';
          if (y === viewYear) cls += ' brio-datepicker-year-cell--selected';
          return <button key={y} type="button" className={cls} onClick={() => selectYear(y)}>{y}</button>;
        })}
      </div>
    );
  }

  const triggerCls = `brio-datepicker-trigger${!value ? ' brio-datepicker-trigger--placeholder' : ''} ${className}`;

  // Compute fixed position to escape stacking contexts
  const [mpDropdownPos, setMpDropdownPos] = useState(null);
  useEffect(() => {
    if (!open || !ref.current) { setMpDropdownPos(null); return; }
    const rect = ref.current.getBoundingClientRect();
    setMpDropdownPos({ top: rect.bottom + 4, left: rect.left });
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', ...style }} {...rest}>
      <button
        type="button"
        className={triggerCls}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
      >
        {!value && <CalendarIcon />}
        <span>{formatDisplay ? formatDisplay(value) : formatMonthDisplay(value, placeholder)}</span>
        {clearable && value && (
          <span className="brio-datepicker-trigger__clear" onClick={(e) => { e.stopPropagation(); onChange(''); }}>×</span>
        )}
      </button>
      {open && mpDropdownPos && (
        <div className="brio-datepicker-dropdown" style={{ position: 'fixed', ...mpDropdownPos }}>
          <div className="brio-datepicker-header">
            <div className="brio-datepicker-nav">
              {viewMode === 'months' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y - 1)}><ChevronLeft /></button>}
              {viewMode === 'years' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y - 12)}><ChevronLeft /></button>}
            </div>
            <span
              className="brio-datepicker-header__title"
              onClick={() => setViewMode(viewMode === 'months' ? 'years' : 'months')}
            >
              {viewMode === 'months' && `${viewYear}`}
              {viewMode === 'years' && `${viewYear - 4} – ${viewYear + 7}`}
            </span>
            <div className="brio-datepicker-nav">
              {viewMode === 'months' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y + 1)}><ChevronRight /></button>}
              {viewMode === 'years' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y + 12)}><ChevronRight /></button>}
            </div>
          </div>
          {viewMode === 'months' && renderMonths()}
          {viewMode === 'years' && renderYears()}
          {clearable && value && (
            <div className="brio-datepicker-footer">
              <span />
              <button type="button" className="brio-datepicker-footer__btn" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// BrioDateRangePicker - Start/end date range with drag support
// ============================================================================

/**
 * BrioDateRangePicker
 * @param {string} startDate - ISO date string (YYYY-MM-DD)
 * @param {string} endDate - ISO date string (YYYY-MM-DD)
 * @param {function} onChangeStart - called with YYYY-MM-DD or ''
 * @param {function} onChangeEnd - called with YYYY-MM-DD or ''
 * @param {function} [onChangeRange] - called with { start, end } when either changes
 * @param {string} [placeholder]
 * @param {string} [className]
 * @param {boolean} [disabled]
 * @param {boolean} [clearable]
 * @param {object} [style]
 */
export function BrioDateRangePicker({ startDate, endDate, onChangeStart, onChangeEnd, onChangeRange, placeholder, className = '', disabled, clearable = true, style, ...rest }) {
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState(null); // null | 'start' | 'end'
  const [hoverDate, setHoverDate] = useState(null);
  const [viewMode, setViewMode] = useState('days');
  const startP = parseDateStr(startDate);
  const endP = parseDateStr(endDate);
  const today = todayObj();
  const [viewYear, setViewYear] = useState(startP ? startP.year : today.year);
  const [viewMonth, setViewMonth] = useState(startP ? startP.month : today.month);
  const ref = useRef(null);

  const closeHandler = useCallback(() => { setOpen(false); setSelecting(null); setHoverDate(null); setViewMode('days'); }, []);
  useClickOutside(ref, closeHandler);

  useEffect(() => {
    const p = parseDateStr(startDate);
    if (p) { setViewYear(p.year); setViewMonth(p.month); }
  }, [startDate]);

  function emitChange(s, e) {
    if (onChangeStart) onChangeStart(s);
    if (onChangeEnd) onChangeEnd(e);
    if (onChangeRange) onChangeRange({ start: s, end: e });
  }

  function dayVal(y, m, d) { return y * 10000 + m * 100 + d; }

  function handleDayClick(year, month, day) {
    const dateStr = toDateStr(year, month, day);
    if (!selecting || selecting === 'start') {
      // First click: set start, wait for end
      emitChange(dateStr, '');
      setSelecting('end');
    } else {
      // Second click: set end (ensure start < end)
      const sP = parseDateStr(startDate);
      if (sP && dayVal(year, month, day) < dayVal(sP.year, sP.month, sP.day)) {
        emitChange(dateStr, startDate);
      } else {
        emitChange(startDate, dateStr);
      }
      setSelecting(null);
      setHoverDate(null);
      setOpen(false);
    }
  }

  function navMonth(delta) {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setViewMonth(m);
    setViewYear(y);
  }

  function selectMonthView(m) { setViewMonth(m); setViewMode('days'); }
  function selectYearView(y) { setViewYear(y); setViewMode('months'); }

  function isInRange(y, m, d) {
    const v = dayVal(y, m, d);
    const sVal = startP ? dayVal(startP.year, startP.month, startP.day) : null;
    const eVal = endP ? dayVal(endP.year, endP.month, endP.day) : null;
    if (sVal && eVal) return v > sVal && v < eVal;
    // During selection: highlight between start and hover
    if (selecting === 'end' && sVal && hoverDate) {
      const hVal = dayVal(hoverDate.year, hoverDate.month, hoverDate.day);
      const lo = Math.min(sVal, hVal);
      const hi = Math.max(sVal, hVal);
      return v > lo && v < hi;
    }
    return false;
  }

  function isRangeEnd(y, m, d) {
    if (selecting === 'end' && hoverDate) {
      return y === hoverDate.year && m === hoverDate.month && d === hoverDate.day;
    }
    return endP && isSameDay(endP, { year: y, month: m, day: d });
  }

  function renderDays() {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfWeek(viewYear, viewMonth);
    const prevMonth = viewMonth === 0 ? 11 : viewMonth - 1;
    const prevYear = viewMonth === 0 ? viewYear - 1 : viewYear;
    const daysInPrev = getDaysInMonth(prevYear, prevMonth);
    const cells = [];

    for (let i = firstDay - 1; i >= 0; i--) {
      const d = daysInPrev - i;
      cells.push(
        <button key={`p${d}`} type="button" className="brio-datepicker-day brio-datepicker-day--outside"
          onClick={() => handleDayClick(prevYear, prevMonth, d)}
          onMouseEnter={() => selecting === 'end' && setHoverDate({ year: prevYear, month: prevMonth, day: d })}
        >{d}</button>
      );
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const isToday = viewYear === today.year && viewMonth === today.month && d === today.day;
      const isStart = startP && isSameDay(startP, { year: viewYear, month: viewMonth, day: d });
      const isEnd = isRangeEnd(viewYear, viewMonth, d);
      const inRange = isInRange(viewYear, viewMonth, d);
      let cls = 'brio-datepicker-day';
      if (isStart) cls += ' brio-datepicker-day--range-start';
      else if (isEnd) cls += ' brio-datepicker-day--range-end';
      else if (inRange) cls += ' brio-datepicker-day--in-range';
      else if (isToday) cls += ' brio-datepicker-day--today';
      cells.push(
        <button key={d} type="button" className={cls}
          onClick={() => handleDayClick(viewYear, viewMonth, d)}
          onMouseEnter={() => selecting === 'end' && setHoverDate({ year: viewYear, month: viewMonth, day: d })}
        >{d}</button>
      );
    }

    const remaining = (7 - (cells.length % 7)) % 7;
    const nextMonth = viewMonth === 11 ? 0 : viewMonth + 1;
    const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear;
    for (let d = 1; d <= remaining; d++) {
      cells.push(
        <button key={`n${d}`} type="button" className="brio-datepicker-day brio-datepicker-day--outside"
          onClick={() => handleDayClick(nextYear, nextMonth, d)}
          onMouseEnter={() => selecting === 'end' && setHoverDate({ year: nextYear, month: nextMonth, day: d })}
        >{d}</button>
      );
    }

    return (
      <>
        <div className="brio-datepicker-weekdays">
          {WEEKDAY_LABELS.map(w => <div key={w} className="brio-datepicker-weekday">{w}</div>)}
        </div>
        <div className="brio-datepicker-days">{cells}</div>
      </>
    );
  }

  function renderMonths() {
    return (
      <div className="brio-datepicker-months">
        {MONTH_LABELS.map((label, i) => {
          let cls = 'brio-datepicker-month-cell';
          if (i === viewMonth) cls += ' brio-datepicker-month-cell--selected';
          return <button key={i} type="button" className={cls} onClick={() => selectMonthView(i)}>{label}</button>;
        })}
      </div>
    );
  }

  function renderYears() {
    const base = viewYear - 4;
    return (
      <div className="brio-datepicker-years">
        {Array.from({ length: 12 }, (_, i) => {
          const y = base + i;
          let cls = 'brio-datepicker-year-cell';
          if (y === viewYear) cls += ' brio-datepicker-year-cell--selected';
          return <button key={y} type="button" className={cls} onClick={() => selectYearView(y)}>{y}</button>;
        })}
      </div>
    );
  }

  const displayStart = startDate ? formatDisplay(startDate) : 'Start';
  const displayEnd = endDate ? formatDisplay(endDate) : 'End';
  const triggerCls = `brio-daterange-trigger${!startDate && !endDate ? ' brio-datepicker-trigger--placeholder' : ''} ${className}`;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', ...style }} {...rest}>
      <button
        type="button"
        className={triggerCls}
        onClick={() => { if (!disabled) { setOpen(!open); if (!open) setSelecting('start'); } }}
        disabled={disabled}
      >
        {!startDate && !endDate && <CalendarIcon />}
        <span>{displayStart}</span>
        <span className="brio-daterange-trigger__sep">→</span>
        <span>{displayEnd}</span>
        {clearable && (startDate || endDate) && (
          <span className="brio-datepicker-trigger__clear" onClick={(e) => { e.stopPropagation(); emitChange('', ''); }}>×</span>
        )}
      </button>
      {open && (
        <div className="brio-datepicker-dropdown" style={{ minWidth: '300px' }}>
          <div className="brio-datepicker-header">
            <div className="brio-datepicker-nav">
              {viewMode === 'days' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => navMonth(-1)}><ChevronLeft /></button>}
              {viewMode === 'years' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y - 12)}><ChevronLeft /></button>}
            </div>
            <span
              className="brio-datepicker-header__title"
              onClick={() => setViewMode(viewMode === 'days' ? 'months' : viewMode === 'months' ? 'years' : 'days')}
            >
              {viewMode === 'days' && `${MONTH_LABELS_FULL[viewMonth]} ${viewYear}`}
              {viewMode === 'months' && `${viewYear}`}
              {viewMode === 'years' && `${viewYear - 4} – ${viewYear + 7}`}
            </span>
            <div className="brio-datepicker-nav">
              {viewMode === 'days' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => navMonth(1)}><ChevronRight /></button>}
              {viewMode === 'years' && <button type="button" className="brio-datepicker-nav__btn" onClick={() => setViewYear(y => y + 12)}><ChevronRight /></button>}
            </div>
          </div>
          {selecting && <div style={{ textAlign: 'center', fontSize: 'var(--brio-font-size-s)', color: 'var(--brio-grey)', marginBottom: '4px' }}>
            {selecting === 'start' ? 'Select start date' : 'Select end date'}
          </div>}
          {viewMode === 'days' && renderDays()}
          {viewMode === 'months' && renderMonths()}
          {viewMode === 'years' && renderYears()}
          <div className="brio-datepicker-footer">
            <span />
            {clearable && (startDate || endDate) && (
              <button type="button" className="brio-datepicker-footer__btn" onClick={() => { emitChange('', ''); setOpen(false); }}>Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Reference to CSS variables for use in JavaScript
 * Use these instead of hardcoded hex values
 */
export const brioTokens = {
  colors: {
    gold: 'var(--brio-gold)',
    goldHover: 'var(--brio-gold-hover)',
    goldLight: 'var(--brio-gold-light)',
    white: 'var(--brio-white)',
    black: 'var(--brio-black)',
    grey: 'var(--brio-grey)',
    greyLight: 'var(--brio-grey-light)',
    border: 'var(--brio-border)',
    green: 'var(--brio-green)',
    greenLight: 'var(--brio-green-light)',
    red: 'var(--brio-red)',
    redLight: 'var(--brio-red-light)',
    orange: 'var(--brio-orange)',
    orangeLight: 'var(--brio-orange-light)',
    yellow: 'var(--brio-yellow)',
    yellowLight: 'var(--brio-yellow-light)',
    teal: 'var(--brio-teal)',
    tealLight: 'var(--brio-teal-light)',
  },
  layout: {
    maxWidthContent: 'var(--brio-maxw-content)',
    maxWidthContainer: 'var(--brio-maxw-container)',
    maxWidthTable: 'var(--brio-maxw-table)',
  },
  spacing: {
    xs: 'var(--brio-space-xs)',
    s: 'var(--brio-space-s)',
    m: 'var(--brio-space-m)',
    l: 'var(--brio-space-l)',
    xl: 'var(--brio-space-xl)',
  }
};
